import React, { ReactElement, ReactNode } from 'react';
import { useIntl } from 'react-intl';
import ArticleIcon from '@fuww/library/src/Icons/Article';
import BoltIcon from '@fuww/library/src/Icons/Bolt';
import CampaignIcon from '@fuww/library/src/Icons/Campaign';
import DashboardIcon from '@fuww/library/src/Icons/Dashboard';
import InsightsIcon from '@fuww/library/src/Icons/Insights';
import SchoolIcon from '@fuww/library/src/Icons/School';
import StorefrontIcon from '@fuww/library/src/Icons/Storefront';
import WebIcon from '@fuww/library/src/Icons/Web';
import WorkIcon from '@fuww/library/src/Icons/Work';
import FeatureCard from '@fuww/library/src/FeatureCard';
import FeatureCardGrid from '@fuww/library/src/FeatureCard/Grid';
import Checklist from '@fuww/library/src/Checklist';
import PremiumEmployerLayout from '@fuww/library/src/PremiumEmployerLayout';
import Head from '../components/Head';
import Layout from '../components/Layout';
import PackageTierComparison from '../components/PackageTierComparison';
import PremiumEmployerHero from '../components/PremiumEmployerHero';
import TextWithImage from '../components/TextWithImage';
import FeaturedEmployerCards
  from '../components/Company/DynamicFeaturedEmployerCards';
import withApollo from '../lib/apollo';
import createCompanyBreadcrumbs from '../lib/createCompanyBreadcrumbs';
import getSourceSet from '../lib/getSourceSet';
import { textWithImageConfigurations } from '../lib/imageConfigurations';
import messages from '../lib/messages.json';

const imageUrls = {
  benefits: [
    'https://r.fashionunited.com/KQdhst-QCTj_07oUquQRwq17DZn5mvTRrM8k8F-23xE/resize:fill-down:42:28:0/gravity:ce/quality:10/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvcHJlbWl1bS1lbXBsb3llci1wcm9kdWN0LnBuZw',
    'https://r.fashionunited.com/2s1sIHrCs5DzSLxTSkEBJEuiE8dI36c4e2GPentAS0E/resize:fill-down:360:240:0/gravity:ce/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvcHJlbWl1bS1lbXBsb3llci1wcm9kdWN0LnBuZw',
    'https://r.fashionunited.com/ZRyvCRuvxf1zEPwzHAhtxjTqvHq79APdT0rifG0y1vc/resize:fill-down:460:307:0/gravity:ce/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvcHJlbWl1bS1lbXBsb3llci1wcm9kdWN0LnBuZw',
    'https://r.fashionunited.com/HDAo9dDPIieN1F0fx79jac7yk7owEnFXEAQI_MW_O1k/resize:fill-down:920:614:0/gravity:ce/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvcHJlbWl1bS1lbXBsb3llci1wcm9kdWN0LnBuZw',
  ],
};

interface FeatureProperties {
  id: string;
  icon: ReactNode;
  titleKey: keyof typeof messages;
  bodyKey: keyof typeof messages;
  tagKey: keyof typeof messages;
}

const features: FeatureProperties[] = [
  {
    id: 'jobPostings',
    icon: <WorkIcon />,
    titleKey: 'premiumEmployer.jobPostingsTitle',
    bodyKey: 'premiumEmployer.jobPostingsBody',
    tagKey: 'premiumEmployer.jobPostingsTag',
  },
  {
    id: 'automatedPosting',
    icon: <BoltIcon />,
    titleKey: 'premiumEmployer.automatedPostingTitle',
    bodyKey: 'premiumEmployer.automatedPostingBody',
    tagKey: 'premiumEmployer.automatedPostingTag',
  },
  {
    id: 'internships',
    icon: <SchoolIcon />,
    titleKey: 'premiumEmployer.internshipsTitle',
    bodyKey: 'premiumEmployer.internshipsBody',
    tagKey: 'premiumEmployer.internshipsTag',
  },
  {
    id: 'employerPage',
    icon: <StorefrontIcon />,
    titleKey: 'premiumEmployer.employerPageTitle',
    bodyKey: 'premiumEmployer.employerPageBody',
    tagKey: 'premiumEmployer.employerPageTag',
  },
  {
    id: 'jobsUnderArticles',
    icon: <ArticleIcon />,
    titleKey: 'premiumEmployer.jobsUnderArticlesTitle',
    bodyKey: 'premiumEmployer.jobsUnderArticlesBody',
    tagKey: 'premiumEmployer.jobsUnderArticlesTag',
  },
  {
    id: 'executiveNews',
    icon: <InsightsIcon />,
    titleKey: 'premiumEmployer.executiveNewsTitle',
    bodyKey: 'premiumEmployer.executiveNewsBody',
    tagKey: 'premiumEmployer.executiveNewsTag',
  },
  {
    id: 'pressReleases',
    icon: <CampaignIcon />,
    titleKey: 'premiumEmployer.pressReleasesTitle',
    bodyKey: 'premiumEmployer.pressReleasesBody',
    tagKey: 'premiumEmployer.pressReleasesTag',
  },
  {
    id: 'atsLite',
    icon: <DashboardIcon />,
    titleKey: 'premiumEmployer.atsLiteTitle',
    bodyKey: 'premiumEmployer.atsLiteBody',
    tagKey: 'premiumEmployer.atsLiteTag',
  },
  {
    id: 'noCodeEditor',
    icon: <WebIcon />,
    titleKey: 'premiumEmployer.noCodeEditorTitle',
    bodyKey: 'premiumEmployer.noCodeEditorBody',
    tagKey: 'premiumEmployer.noCodeEditorTag',
  },
];

const PremiumEmployerStatusPage = () => {
  const intl = useIntl();

  const breadcrumbs = createCompanyBreadcrumbs({
    intl,
    crumbRoute: 'premium-employer-status',
    crumbLabel: intl.formatMessage(messages['premiumEmployer.header']),
  });

  return (
    <>
      <Head
        title={intl.formatMessage(messages['premiumEmployer.header'])}
        description={intl.formatMessage(
          messages['premiumEmployer.metaDescription'],
        )}
      />
      <PremiumEmployerLayout
        breadcrumbs={breadcrumbs}
        packageTiers={<PackageTierComparison />}
        sectionsTitle={intl.formatMessage(
          messages['premiumEmployer.sectionsTitle'],
        )}
        sectionsDescription={intl.formatMessage(
          messages['premiumEmployer.description'],
        )}
        featureCards={(
          <FeatureCardGrid>
            {features.map(({
              id, icon, titleKey, bodyKey, tagKey,
            }) => (
              <FeatureCard
                key={id}
                icon={icon}
                title={intl.formatMessage(messages[titleKey])}
                description={intl.formatMessage(messages[bodyKey])}
                tag={intl.formatMessage(messages[tagKey])}
              />
            ))}
          </FeatureCardGrid>
        )}
        featuredEmployersTitle={intl.formatMessage(
          messages['premiumEmployer.featuredEmployersTitle'],
        )}
        featuredEmployers={<FeaturedEmployerCards />}
        textWithImageComponents={(
          <TextWithImage
            title={intl.formatMessage(messages['premiumEmployer.benefitsTitle'])}
            description={(
              <>
                {intl.formatMessage(messages['premiumEmployer.benefitsBody'])}
                <Checklist
                  items={[
                    {
                      key: 'pipeline',
                      label: intl.formatMessage(
                        messages['premiumEmployer.benefitsPipeline'],
                      ),
                    },
                    {
                      key: 'notes',
                      label: intl.formatMessage(
                        messages['premiumEmployer.benefitsNotes'],
                      ),
                    },
                    {
                      key: 'gdpr',
                      label: intl.formatMessage(
                        messages['premiumEmployer.benefitsGdpr'],
                      ),
                    },
                    {
                      key: 'reports',
                      label: intl.formatMessage(
                        messages['premiumEmployer.benefitsReports'],
                      ),
                    },
                  ]}
                />
              </>
            )}
            buttonLabel={intl.formatMessage(
              messages['premiumEmployer.buttonLabel'],
            )}
            buttonHref="mailto:jobs@fashionunited.com"
            buttonVariant="contained"
            imagePosition="left"
            imageUrls={imageUrls.benefits}
            srcSet={getSourceSet(
              textWithImageConfigurations,
              imageUrls.benefits,
            )}
          />
        )}
        contactContent={(
          <div
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: intl.formatMessage(
                messages['submitJobPostings.callToActionText'],
              ),
            }}
          />
        )}
      />
    </>
  );
};

PremiumEmployerStatusPage.getLayout = (page: ReactElement) => (
  <Layout header={<PremiumEmployerHero />}>
    {page}
  </Layout>
);

export default withApollo(PremiumEmployerStatusPage);
